import React, { useEffect, useState } from "react"
import { translate } from "../../../../selectors/translations"
import { useSelector } from "react-redux"
import MedicalTestItem from "../MedicalTestItem/MedicalTestItem"
import { ReactComponent as TestIcon } from '../../../../assets/img/icons/clock-icon.svg'
import './PatientAnamnesisForm.scss'
import useTranslate from "../../../../hooks/useTranslate"
import { getQuestioners } from "../../../../selectors/patientProfile"
import {
  AvailableQuestionersDto,
  ContentVisibilityType,
  HiddenFroInstitutionVisibilityDto,
  QuestionerType
} from "../../../../sdk"
import { isDefined } from "../../../../sdk/core/request"
import { institutionAccess } from "../../../../selectors/institutions"

const homeScreenMapConfigs = [
  {
    title: 'patientAccount.newPatient.patientTasks.cognitiveTest',
    slug: QuestionerType.COGNITIVE
  },
  {
    title: 'patientAccount.newPatient.patientTasks.symptomSurvey',
    slug: QuestionerType.SYMPTOM_SURVEY
  },
  {
    title: 'FAQ',
    slug: QuestionerType.FAQ
  },
  {
    title: 'patientAccount.newPatient.patientTasks.medicalHistory',
    slug: QuestionerType.ANAMNESIS
  },
]

const anamnesisSectionsGroups = [
  {
    title: 'anamnesisFlexNew.categories.social',
    sectionSlugs: ['social-background-section', 'cognitive-functions-section', 'dietary-habits-section', 'exercise-section'],
  },
  {
    title: 'anamnesisFlexNew.categories.screening',
    sectionSlugs: ['concentration-section', 'depression-section', 'stress-questions-section', 'alcohol-drugs-section', 'ptsd-questions-section']
  },
  {
    title: 'anamnesisFlexNew.categories.medical',
    sectionSlugs: ['healthstatus-section', 'prescriptivedrugs-section', 'allgergy-section', 'hereditary-section']
  },
  {
    title: 'anamnesisFlexNew.categories.screening',
    sectionSlugs: ['anxiety']
  }
]

interface IProps {
  onFormChange: (data: any) => void;
  homeScreenMap: AvailableQuestionersDto;
  patientInstitution: string;
  children?: React.ReactNode;
}

const PatientAnamnesisForm: React.FC<IProps> = (props: IProps): JSX.Element => {
  const {homeScreenMap, children, onFormChange, patientInstitution} = props
  const [availableQuestioners, changeAvailableQuestioners] = useState(homeScreenMap)
  const translateLocalazy = useTranslate()
  const access = useSelector(institutionAccess)
  const anamnesis = useSelector(getQuestioners).find(q => q.kind == QuestionerType.ANAMNESIS)
  // sets initial when loaded
  useEffect(() => {
    changeAvailableQuestioners(homeScreenMap)
  }, [homeScreenMap])

  const handleAnamnesisSectionClick = (slug: string) => {
    const currentSelection = availableQuestioners.anamnesis || []
    const newAnamnesisConfig = currentSelection?.includes(slug)
      ? currentSelection.filter(s => s != slug)
      : [...currentSelection!, slug]
    const update = {...availableQuestioners, anamnesis: newAnamnesisConfig.length ? newAnamnesisConfig : null}
    changeAvailableQuestioners(update)
    onFormChange(update)
  }

  const handleQuestionerClick = (slug: string) => {
    let update
    if (availableQuestioners[slug]) {
      update = {...availableQuestioners, [slug]: null}
    } else {
      const allSections = slug === 'anamnesis' ? anamnesis?.sections.map(e => e.slug) || [] : []
      update = {...availableQuestioners, [slug]: allSections}
    }
    changeAvailableQuestioners(update)
    onFormChange(update)
  }

  const isQuestionerEnabled = (slug: string): boolean => availableQuestioners && Boolean(availableQuestioners[slug])
  const isSectionEnabled = (slug: string): boolean => availableQuestioners && Boolean(availableQuestioners['anamnesis']?.includes(slug))
  const availableAnamnesisSections = anamnesisSectionsGroups.map(s => {
    return {
      title: s.title,
      sectionSlugs: s.sectionSlugs.filter(slug => {
        const section = anamnesis?.sections.find(ss => ss.slug === slug)
        return !(section?.visibility || [])
          .find(v => v.kind === ContentVisibilityType.HIDDEN_FOR_INSTITUTION && (v as HiddenFroInstitutionVisibilityDto).institution === patientInstitution)
      })
    }
  })

  return (
    <div className="card-box patient-info">
      <div>
        <div className="medical-items__header">
          <div className="box-title">
            <i className="icon-holder patients-title_icon-holder"><TestIcon color="#ffffff"/></i>
            <h3>{translateLocalazy('patient.administrate.select-tests')}</h3>
          </div>
        </div>
        <div className="medical-items">
          {homeScreenMapConfigs.map(item => {
            let available = false
            switch (item.slug) {
              case QuestionerType.ANAMNESIS:
                available = access.anamnesisTest
                break
              case QuestionerType.COGNITIVE:
                available = access.cognitiveTest
                break
              case QuestionerType.SYMPTOM_SURVEY:
                available = access.symptomSurveyTest
                break
              case QuestionerType.FAQ:
                available = true
                break
            }
            return (
              <div className="item--12 item-sm-6 item-md-4" key={item.slug}>
                <div className="medical-items__items-group-item">
                  <MedicalTestItem
                    title={translate(item.title)}
                    onClick={handleQuestionerClick}
                    id={item.slug}
                    available={available}
                    isSelected={isQuestionerEnabled(item.slug)}></MedicalTestItem>
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <div>
        <div className="medical-items__holder">
          {availableAnamnesisSections.map(column => (
            <div className="item--12 item-sm-6 item-lg-3 medical-items__items-group" key={column.title}>
              <h3 className="medical-items__items-group-heading">{translate(column.title)}</h3>
              {column.sectionSlugs.map(slug => anamnesis?.sections
                .find(s => s.slug === slug))
                .filter(isDefined)
                .map(section =>
                  (
                    <div className="medical-items__items-group-item sub-items" key={section.slug}>
                      <MedicalTestItem
                        available={access.anamnesisTest}
                        title={translateLocalazy('questioner.anamnesis.section.' + section.slug)}
                        onClick={handleAnamnesisSectionClick}
                        id={section.slug}
                        isSelected={isSectionEnabled(section.slug)}></MedicalTestItem>
                    </div>

                  ))}
            </div>
          ))}
        </div>
        {children ? <div>{children}</div> : null}
      </div>
    </div>
  )
}

export default PatientAnamnesisForm
